import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { AccountStatement } from './accountStatement';
import type { StatementViewRow } from './useAccountStatement';

const NAVY: [number, number, number] = [17, 53, 84];
const INCOME: [number, number, number] = [22, 163, 74];
const EXPENSE: [number, number, number] = [220, 38, 38];

const fmtBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

export function accountSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface Input {
  accountName: string;
  isCreditCard: boolean;
  clientName: string;
  fromDate: Date;
  toDate: Date;
  statement: AccountStatement;
  rows: StatementViewRow[];
}

export function exportAccountStatementPdf({
  accountName,
  isCreditCard,
  clientName,
  fromDate,
  toDate,
  statement,
  rows,
}: Input): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 90, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(`${isCreditCard ? 'Extrato do Cartão' : 'Extrato'} — ${accountName}`, margin, 40);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(clientName, margin, 60);

  doc.setFontSize(9);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
    pageWidth - margin,
    60,
    { align: 'right' },
  );

  // Período
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  let cursorY = 115;

  const period = `${format(fromDate, 'dd/MM/yyyy', { locale: ptBR })} a ${format(
    toDate,
    'dd/MM/yyyy',
    { locale: ptBR },
  )}`;
  doc.setFont('helvetica', 'bold');
  doc.text('Período: ', margin, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(period, margin + 50, cursorY);
  cursorY += 20;

  // Resumo
  doc.setFillColor(245, 247, 250);
  doc.rect(margin, cursorY, pageWidth - 2 * margin, 56, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Saldo anterior', margin + 12, cursorY + 18);
  doc.text('Entradas', margin + 150, cursorY + 18);
  doc.text('Saídas', margin + 280, cursorY + 18);
  doc.text('Saldo final', margin + 400, cursorY + 18);

  doc.setFontSize(13);
  doc.setTextColor(20, 20, 20);
  doc.text(fmtBrl(statement.openingBalance), margin + 12, cursorY + 42);

  doc.setTextColor(...INCOME);
  doc.text(fmtBrl(statement.totalIn), margin + 150, cursorY + 42);

  doc.setTextColor(...EXPENSE);
  doc.text(fmtBrl(statement.totalOut), margin + 280, cursorY + 42);

  doc.setTextColor(...(statement.closingBalance < 0 ? EXPENSE : NAVY));
  doc.text(fmtBrl(statement.closingBalance), margin + 400, cursorY + 42);

  cursorY += 76;

  // Tabela: primeira linha Saldo anterior, última Saldo final
  const OPENING_ROW = 0;
  const closingRowIndex = rows.length + 1;

  const body: string[][] = [
    ['', 'Saldo anterior', '', '', '', fmtBrl(statement.openingBalance)],
    ...rows.map((r) => {
      const description = r.counterpartAccountName
        ? `${r.entry.tx.description} (${r.entry.direction === 'out' ? '→' : '←'} ${r.counterpartAccountName})`
        : r.entry.tx.description;
      return [
        format(r.entry.refDate, 'dd/MM/yyyy', { locale: ptBR }),
        description,
        r.categoryName,
        r.entry.direction === 'in' ? fmtBrl(r.entry.amount) : '',
        r.entry.direction === 'out' ? fmtBrl(r.entry.amount) : '',
        fmtBrl(r.entry.runningBalance),
      ];
    }),
    ['', 'Saldo final', '', '', '', fmtBrl(statement.closingBalance)],
  ];

  autoTable(doc, {
    startY: cursorY,
    margin: { left: margin, right: margin },
    head: [['Data', 'Descrição', 'Categoria', 'Entrada', 'Saída', 'Saldo']],
    body,
    headStyles: {
      fillColor: NAVY,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 62 },
      3: { halign: 'right', textColor: INCOME },
      4: { halign: 'right', textColor: EXPENSE },
      5: { halign: 'right', fontStyle: 'bold' },
    },
    didParseCell: (hookData) => {
      if (hookData.section !== 'body') return;
      const { row, cell, column } = hookData;
      if (row.index === OPENING_ROW || row.index === closingRowIndex) {
        cell.styles.fontStyle = 'bold';
        cell.styles.fillColor = [235, 240, 246];
        cell.styles.textColor = NAVY;
      }
      if (column.index === 5 && typeof cell.raw === 'string' && cell.raw.includes('-')) {
        cell.styles.textColor = EXPENSE;
      }
    },
    didDrawPage: () => {
      const pageCount = doc.getNumberOfPages();
      const currentPage = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Página ${currentPage} de ${pageCount}`,
        pageWidth - margin,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' },
      );
    },
  });

  const filename = `extrato-${accountSlug(accountName)}-${format(fromDate, 'yyyy-MM-dd')}-a-${format(
    toDate,
    'yyyy-MM-dd',
  )}.pdf`;
  doc.save(filename);
}
